// src/pages/Users/User.jsx
import React, { useEffect, useState, useCallback } from 'react';
import Header from '../../components/layout/Header/Header';
import Sidebar from '../../components/layout/Sidebar/Sidebar';
import './User.css';
import SearchableSelect from '../../components/common/SearchableSelect';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from "../../utils/toast"; // ← NEW IMPORT

const User = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: '',
    emiratesName: '',
    emiratesCode: '',
    creditLimit: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [userId, setUserId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [emiratesList, setEmiratesList] = useState([]);
  
  const backendUrl = process.env.REACT_APP_BACKEND_IP;
  const navigate = useNavigate();
  const location = useLocation();

  // Whether the selected role is Sales man (only Sales man role, not Sales Manager)
  const isSalesmanRole = formData.role === "Sales man";

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters long';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please provide a valid email';
    }

    if (!isEdit && !formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (isSalesmanRole) {
      if (formData.creditLimit === '' || isNaN(formData.creditLimit)) {
        newErrors.creditLimit = 'Credit limit is required for salesmen';
      } else if (parseFloat(formData.creditLimit) < 0) {
        newErrors.creditLimit = 'Credit limit cannot be negative';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'emiratesName') {
      const selected = emiratesList.find((em) => em.emiratesName === value);
      setFormData(prev => ({
        ...prev,
        emiratesName: value,
        emiratesCode: selected ? selected.emiratesCode : '',
      }));
      if (errors.emiratesName) setErrors(prev => ({ ...prev, emiratesName: '' }));
      return;
    }

    if (name === 'role') {
      // Clear emirates fields and credit limit when role changes
      setFormData(prev => ({
        ...prev,
        role: value,
        emiratesName: '',
        emiratesCode: '',
        creditLimit: '',
      }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      if (isEdit) {
        // Update existing user
        const updateData = {
          username: formData.username,
          email: formData.email,
          role: formData.role,
          emiratesName: isSalesmanRole ? formData.emiratesName : null,
          emiratesCode: isSalesmanRole ? formData.emiratesCode : null,
          creditLimit: isSalesmanRole ? parseFloat(formData.creditLimit) : undefined,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${backendUrl}/api/users/updateUser/${userId}`, updateData, config);

        toast.success('User updated successfully!');
      } else {
        // Create new user
        await axios.post(`${backendUrl}/api/users/createUser`, {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          emiratesName: isSalesmanRole ? formData.emiratesName : null,
          emiratesCode: isSalesmanRole ? formData.emiratesCode : null,
          creditLimit: isSalesmanRole ? parseFloat(formData.creditLimit) : undefined,
        }, config);

        toast.success('User created successfully!');
      }

      // Navigate after short delay so user sees the success toast
      setTimeout(() => {
        navigate('/userlist');
      }, 1500);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/roles/getAllRoles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      toast.error("Failed to load roles");
    }
  }, [backendUrl]);

  const fetchEmirates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/emirates/getAll`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmiratesList(response.data);
    } catch (error) {
      console.error("Failed to fetch emirates:", error);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchRoles();
    fetchEmirates();
  }, [fetchRoles, fetchEmirates]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editId = searchParams.get('edit');

    if (editId) {
      const loadUserForEdit = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${backendUrl}/api/users/getAllUsers`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const users = response.data;
          const userToEdit = users.find(u => u._id === editId);

          if (userToEdit) {
            setFormData({
              username: userToEdit.username,
              email: userToEdit.email,
              password: '', // Don't pre-fill password
              role: userToEdit.role,
              emiratesName: userToEdit.emiratesName || '',
              emiratesCode: userToEdit.emiratesCode || '',
              creditLimit: (userToEdit.salesmanCreditLimit ?? userToEdit.creditLimit ?? '').toString(),
            });
            setIsEdit(true);
            setUserId(editId);
          } else {
            navigate('/userlist');
          }
        } catch (error) {
          console.error("Failed to fetch user data for edit", error);
          toast.error("Failed to load user data");
          navigate('/userlist');
        }
      };

      loadUserForEdit();
    }
  }, [location.search, navigate, backendUrl]);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await axios.get(`${backendUrl}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setUser(response.data.user || response.data);
      } catch (error) {
        console.error("Failed to load user", error);
        localStorage.removeItem('token');
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, [navigate, backendUrl]);

  if (loading) {
    return <div className="loading">Loading user information...</div>;
  }

  if (!user) {
    return null;
  }

  return (
    <div className="user-registration-layout">
      <Header 
        sidebarOpen={sidebarOpen} 
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} 
        user={user}
      />
      <Sidebar 
        isOpen={sidebarOpen} 
        activeItem="Users" 
        onSetActiveItem={() => {}} 
        onClose={() => setSidebarOpen(false)}
        user={user}
      />
      <main className={`main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="registration-card">
          <h1>{isEdit ? 'Edit User' : 'Create New User'}</h1>

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="username">Full Name</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  aria-invalid={!!errors.username}
                  aria-describedby={errors.username ? "username-error" : undefined}
                />
                {errors.username && (
                  <p id="username-error" className="error-text" role="alert">
                    {errors.username}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="error-text" role="alert">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="form-group">
                <label htmlFor="password" style={{ marginTop: "10px" }}>Password {isEdit && <div className="optional-text">(leave blank to keep current)</div>}</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={isEdit ? "Enter new password" : ""}
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? "password-error" : undefined}
                />
                {errors.password && (
                  <p id="password-error" className="error-text" role="alert">
                    {errors.password}
                  </p>
                )}
              </div>

            <div className="form-group">
              <label htmlFor="role">Role</label>
              <SearchableSelect
                id="role"
                options={roles.map(role => ({ value: role.name, label: role.name }))}
                value={formData.role}
                onChange={(val) => handleChange({ target: { name: "role", value: val } })}
                placeholder="Select a role"
              />
              {errors.role && (
                <p id="role-error" className="error-text" role="alert">
                  {errors.role}
                </p>
              )}
            </div>

            {isSalesmanRole && (
              <div style={{ marginTop: '20px' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="emiratesName">Emirates</label>
                    <SearchableSelect
                      id="emiratesName"
                      options={emiratesList.map(em => ({ value: em.emiratesName, label: em.emiratesName }))}
                      value={formData.emiratesName}
                      onChange={(val) => handleChange({ target: { name: "emiratesName", value: val } })}
                      placeholder="Select Emirates"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="emiratesCode">Emirates Code</label>
                    <input
                      id="emiratesCode"
                      name="emiratesCode"
                      type="text"
                      value={formData.emiratesCode}
                      readOnly
                      placeholder="Auto-filled from Emirates selection"
                      style={{ backgroundColor: '#f8fafc', cursor: 'default' }}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="creditLimit">Credit Limit (AED)</label>
                    <input
                      id="creditLimit"
                      name="creditLimit"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.creditLimit}
                      onChange={handleChange}
                      aria-invalid={!!errors.creditLimit}
                      aria-describedby={errors.creditLimit ? 'creditLimit-error' : undefined}
                    />
                    {errors.creditLimit && (
                      <p id="creditLimit-error" className="error-text" role="alert">
                        {errors.creditLimit}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              className="submit-button"
              disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update User' : 'Create User')}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default User;